import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Season, SeasonCreateRequest, SeasonUpdateRequest } from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { useCurrentUserStore } from '@/stores/current-user-store.ts';
import { AllotmentRolesConstants as Roles } from '@/stores/definitions.js';
import { getDropdownOptions } from '@/modules/dataParser.js';
import {
  buildAbstractMap,
  toDateOnly,
  toDateOnlyString,
  toNullableNumber,
} from './common.ts';

type LegacySeason = Omit<Season, 'startDate' | 'endDate'> & {
  endDate?: string | null;
  startDate?: string | null;
};

type LegacySeasonPayload = Partial<LegacySeason> & Record<string, unknown> & {
  includeSeason?: boolean;
};

function toTourOperatorIds(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(entry => Number(entry))
    .filter(entry => Number.isFinite(entry));
}

function normalizeSeason(entry: Season | null | undefined): LegacySeason | undefined {
  if (!entry) {
    return undefined;
  }

  return {
    ...entry,
    endDate: toDateOnlyString(entry.endDate),
    startDate: toDateOnlyString(entry.startDate),
    tourOperatorIds: toTourOperatorIds(entry.tourOperatorIds),
  };
}

function buildCreateRequest(data: LegacySeasonPayload): SeasonCreateRequest {
  return {
    code: data.code,
    duplicateSeasonId: toNullableNumber(data.duplicateSeasonId),
    endDate: toDateOnly(data.endDate),
    name: data.name,
    startDate: toDateOnly(data.startDate),
    tourOperatorIds: toTourOperatorIds(data.tourOperatorIds),
  } as SeasonCreateRequest;
}

function buildUpdateRequest(data: LegacySeasonPayload): SeasonUpdateRequest {
  return {
    code: data.code,
    endDate: toDateOnly(data.endDate),
    id: data.id,
    name: data.name,
    startDate: toDateOnly(data.startDate),
    tourOperatorIds: toTourOperatorIds(data.tourOperatorIds),
    version: data.version,
  } as SeasonUpdateRequest;
}

export const useSeasonsStore = defineStore('seasons', () => {
  const apiClient = useApiClient();
  const currentUserStore = useCurrentUserStore();

  const list = ref<LegacySeason[]>([]);
  const abstract = ref<Record<string, string> | null>(null);
  const added = ref<LegacySeason | null>(null);
  const loading = ref<boolean>(true);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function add(data: LegacySeasonPayload): Promise<LegacySeason | undefined> {
    const response = await apiClient.season.post(buildCreateRequest(data));
    added.value = normalizeSeason(response) ?? null;
    return added.value ?? undefined;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.season.byId(id).byIsActive(active).post();
  }

  async function update(data: LegacySeasonPayload): Promise<LegacySeason | undefined> {
    const response = await apiClient.season.put(buildUpdateRequest(data));
    return normalizeSeason(response);
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<LegacySeason[]> {
    loading.value = true;

    try {
      const response = await apiClient.season.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = (response ?? [])
        .map(entry => normalizeSeason(entry))
        .filter((entry): entry is LegacySeason => Boolean(entry));
      abstract.value = buildAbstractMap(list.value, entry => entry.name);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  async function getAbstract({ showOnlyActive = true }: { showOnlyActive?: boolean } = {}): Promise<Record<string, string>> {
    loading.value = true;

    try {
      const currentUser = currentUserStore.currentUser ?? await currentUserStore.get();
      const response = Number(currentUser?.allotmentRoleId) === Roles.TOUR_OPERATOR
        ? await apiClient.season.getForTourOperator.get({
          queryParameters: {
            isActive: showOnlyActive,
          },
        })
        : await apiClient.season.get({
          queryParameters: {
            isActive: showOnlyActive,
          },
        });

      abstract.value = buildAbstractMap(response ?? [], entry => entry.name);
      return abstract.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    abstract,
    added,
    loading,
    options,
    add,
    toggle,
    update,
    get,
    getAbstract,
  };
});
